import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  Tooltip,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { getLongNumberStr } from '@/helpers/common';
import { formatLeaseLengthFromDates } from '@/helpers/leaseHelpers';
import { Concession } from '@/types/lease';
import dayjs from 'dayjs';

// Types matching the public API response
interface PublicCurrentLeaseView {
  monthlyRent: number;
  leaseStartDate: string;
  leaseEndDate: string;
  totalConcessionAmount: number;
}

interface PublicLeaseView {
  id: string;
  unitId: string;
  monthlyRent: number;
  leaseStartDate: string;
  leaseEndDate: string;
  totalConcessionAmount: number;
  concessions: Concession[];
  externalApproval?: {
    approved: boolean;
    approvedAt?: string;
    approvedBy?: string;
    notes?: string;
  };
}

interface PublicUnitView {
  id: string;
  name: string;
  numBedrooms: number;
  sqft: number;
  floor?: number;
  currentLease?: PublicCurrentLeaseView;
}

interface SharedDraftLeaseCardProps {
  draftLease: PublicLeaseView;
  unit: PublicUnitView;
  isSelected: boolean;
  onSelectionChange: (leaseId: string, selected: boolean) => void;
  showConcessionsAsMultiples?: boolean;
  showDifferenceAsPercent?: boolean;
}

// Helper function to convert concession amount to months
function concessionsToMonths(concessionAmount: number, monthlyRent: number): number {
  if (monthlyRent <= 0) return 0;
  return Math.round((concessionAmount / monthlyRent) * 10) / 10;
}

// Helper function to calculate lease length with proper rounding
// Matches the logic in leaseHelpers.ts calculateLeaseLength
function calculateLeaseLengthFromDates(startDateStr: string, endDateStr: string): number {
  const startDate = dayjs(startDateStr);
  const endDate = dayjs(endDateStr);

  if (!startDate.isValid() || !endDate.isValid()) return 0;

  const exactMonths = endDate.diff(startDate, 'month', true);

  // Round to nearest month if within ~3 days (0.1 of a month ≈ 3 days)
  const roundedMonths = Math.round(exactMonths);
  const difference = Math.abs(exactMonths - roundedMonths);

  if (difference <= 0.1) {
    return roundedMonths;
  }

  return exactMonths;
}

// Helper function to calculate lease metrics
function calculateMetrics(
  sqft: number,
  monthlyRent: number,
  leaseStartDate: string,
  leaseEndDate: string,
  totalConcessionAmount: number
) {
  const leaseLength = calculateLeaseLengthFromDates(leaseStartDate, leaseEndDate) || 1;

  const effectiveMonthlyRent = monthlyRent - (totalConcessionAmount / leaseLength);
  const effectiveAnnualRentPerSqft = sqft > 0 ? (effectiveMonthlyRent * 12) / sqft : 0;

  return {
    monthlyRent,
    leaseLength,
    effectiveMonthlyRent,
    effectiveAnnualRentPerSqft,
    concessions: totalConcessionAmount,
    leaseEndDate,
  };
}

export function SharedDraftLeaseCard({
  draftLease,
  unit,
  isSelected,
  onSelectionChange,
  showConcessionsAsMultiples = true,
  showDifferenceAsPercent = true,
}: SharedDraftLeaseCardProps) {
  const sqft = unit.sqft || 1;
  const currentLease = unit.currentLease;

  // Calculate current metrics (from current valid lease)
  const currentMetrics = currentLease
    ? calculateMetrics(
        sqft,
        currentLease.monthlyRent,
        currentLease.leaseStartDate,
        currentLease.leaseEndDate,
        currentLease.totalConcessionAmount
      )
    : null;

  // Calculate draft metrics
  const draftMetrics = calculateMetrics(
    sqft,
    draftLease.monthlyRent,
    draftLease.leaseStartDate,
    draftLease.leaseEndDate,
    draftLease.totalConcessionAmount
  );

  // Calculate changes (if current metrics available)
  const rentIncrease = currentMetrics
    ? draftMetrics.monthlyRent - currentMetrics.monthlyRent
    : 0;
  const rentIncreasePercent = currentMetrics && currentMetrics.monthlyRent > 0
    ? (rentIncrease / currentMetrics.monthlyRent) * 100
    : 0;

  const effectiveRentIncrease = currentMetrics
    ? draftMetrics.effectiveMonthlyRent - currentMetrics.effectiveMonthlyRent
    : 0;
  const effectiveRentIncreasePercent = currentMetrics && currentMetrics.effectiveMonthlyRent > 0
    ? (effectiveRentIncrease / currentMetrics.effectiveMonthlyRent) * 100
    : 0;

  const ppsqfIncrease = currentMetrics
    ? draftMetrics.effectiveAnnualRentPerSqft - currentMetrics.effectiveAnnualRentPerSqft
    : 0;
  const ppsqfIncreasePercent = currentMetrics && currentMetrics.effectiveAnnualRentPerSqft > 0
    ? (ppsqfIncrease / currentMetrics.effectiveAnnualRentPerSqft) * 100
    : 0;

  // Calculate days to expiry
  const currentLeaseEndDate = currentMetrics?.leaseEndDate;
  const draftLeaseEndDate = draftMetrics.leaseEndDate;
  const currentDaysToExpiry = currentLeaseEndDate ? dayjs(currentLeaseEndDate).diff(dayjs(), 'day') : 0;
  const draftDaysToExpiry = draftLeaseEndDate ? dayjs(draftLeaseEndDate).diff(dayjs(), 'day') : 0;

  // Check for external approval
  const externalApproval = draftLease.externalApproval;
  const isExternallyApproved = externalApproval?.approved;

  return (
    <div className={`w-full rounded-md border px-6 py-4 ${
      isExternallyApproved
        ? 'border-green-600/50 bg-green-900/10'
        : isSelected
        ? 'border-blue-500 bg-zinc-900/50'
        : 'border-zinc-600 bg-zinc-900/50'
    }`}>
      {/* Header with unit name and checkbox/approval status */}
      <div className="flex justify-between items-start mb-6">
        <div className="flex items-center gap-3">
          {!isExternallyApproved && (
            <Checkbox
              checked={isSelected}
              onChange={(e) => onSelectionChange(draftLease.id, e.target.checked)}
              sx={{ p: 0 }}
            />
          )}
          <div>
            <div className="flex items-center gap-2">
              <Typography variant="h6" className="font-bold text-blue-100">
                {unit.name || unit.id} - Draft Terms
              </Typography>
              {isExternallyApproved && (
                <Tooltip
                  title={
                    <div>
                      <div>Externally Approved</div>
                      {externalApproval?.approvedBy && (
                        <div>By: {externalApproval.approvedBy}</div>
                      )}
                      {externalApproval?.approvedAt && (
                        <div>On: {dayjs(externalApproval.approvedAt).format('MMM D, YYYY h:mm A')}</div>
                      )}
                      {externalApproval?.notes && (
                        <div>Notes: {externalApproval.notes}</div>
                      )}
                    </div>
                  }
                >
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="Externally Approved"
                    color="success"
                    size="small"
                  />
                </Tooltip>
              )}
            </div>
            <Typography variant="body2" color="textSecondary" className="text-xs">
              {unit.numBedrooms}BR, {unit.sqft} sqft
            </Typography>
          </div>
        </div>
      </div>

      {/* Comparison Table - matching DraftLeaseCard exactly */}
      <div className="overflow-x-auto">
        <Table size="small" sx={{ minWidth: 600 }}>
          <TableHead>
            <TableRow className="border-b border-zinc-600">
              <TableCell className="text-xs font-bold text-zinc-300 py-2">Metric</TableCell>
              <TableCell align="right" className="text-xs font-bold text-zinc-300 py-2">Current</TableCell>
              <TableCell align="right" className="text-xs font-bold text-blue-300 py-2">Proposed</TableCell>
              <TableCell align="right" className="text-xs font-bold text-zinc-300 py-2">Change</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Monthly Rent Row */}
            <TableRow className="border-b border-zinc-700/50">
              <TableCell className="text-xs text-zinc-400 py-2">Monthly Rent</TableCell>
              <TableCell align="right" className="text-sm text-zinc-300 py-2">
                {currentMetrics ? `$${getLongNumberStr(Math.round(currentMetrics.monthlyRent))}` : '—'}
              </TableCell>
              <TableCell align="right" className="text-sm text-blue-100 font-semibold py-2">
                ${getLongNumberStr(Math.round(draftMetrics.monthlyRent))}
              </TableCell>
              <TableCell align="right" className={`text-sm font-semibold py-2 ${
                currentMetrics ? (rentIncreasePercent >= 0 ? 'text-green-400' : 'text-red-400') : 'text-zinc-400'
              }`}>
                {currentMetrics
                  ? showDifferenceAsPercent
                    ? `${rentIncreasePercent >= 0 ? '+' : ''}${rentIncreasePercent.toFixed(1)}%`
                    : `${rentIncrease >= 0 ? '+' : ''}$${getLongNumberStr(Math.round(rentIncrease))}`
                  : '—'}
              </TableCell>
            </TableRow>

            {/* Annual Rent/Sqft Row */}
            <TableRow className="border-b border-zinc-700/50">
              <TableCell className="text-xs text-zinc-400 py-2">Annual Rent/Sqft</TableCell>
              <TableCell align="right" className="text-sm text-zinc-300 py-2">
                {currentMetrics ? `$${currentMetrics.effectiveAnnualRentPerSqft.toFixed(2)}` : '—'}
              </TableCell>
              <TableCell align="right" className="text-sm text-blue-100 font-semibold py-2">
                ${draftMetrics.effectiveAnnualRentPerSqft.toFixed(2)}
              </TableCell>
              <TableCell align="right" className={`text-sm font-semibold py-2 ${
                currentMetrics ? (ppsqfIncreasePercent >= 0 ? 'text-green-400' : 'text-red-400') : 'text-zinc-400'
              }`}>
                {currentMetrics
                  ? showDifferenceAsPercent
                    ? `${ppsqfIncreasePercent >= 0 ? '+' : ''}${ppsqfIncreasePercent.toFixed(1)}%`
                    : `${ppsqfIncrease >= 0 ? '+' : ''}$${ppsqfIncrease.toFixed(2)}`
                  : '—'}
              </TableCell>
            </TableRow>

            {/* Effective Monthly Rent Row */}
            <TableRow className="border-b border-zinc-700/50">
              <TableCell className="text-xs text-zinc-400 py-2">Eff. Monthly Rent</TableCell>
              <TableCell align="right" className="text-sm text-zinc-300 py-2">
                {currentMetrics ? `$${getLongNumberStr(Math.round(currentMetrics.effectiveMonthlyRent))}` : '—'}
              </TableCell>
              <TableCell align="right" className="text-sm text-blue-100 font-semibold py-2">
                ${getLongNumberStr(Math.round(draftMetrics.effectiveMonthlyRent))}
              </TableCell>
              <TableCell align="right" className={`text-sm font-semibold py-2 ${
                currentMetrics ? (effectiveRentIncreasePercent >= 0 ? 'text-green-400' : 'text-red-400') : 'text-zinc-400'
              }`}>
                {currentMetrics
                  ? showDifferenceAsPercent
                    ? `${effectiveRentIncreasePercent >= 0 ? '+' : ''}${effectiveRentIncreasePercent.toFixed(1)}%`
                    : `${effectiveRentIncrease >= 0 ? '+' : ''}$${getLongNumberStr(Math.round(effectiveRentIncrease))}`
                  : '—'}
              </TableCell>
            </TableRow>

            {/* Lease Length Row */}
            <TableRow className="border-b border-zinc-700/50">
              <TableCell className="text-xs text-zinc-400 py-2">Lease Length</TableCell>
              <TableCell align="right" className="text-sm text-zinc-300 py-2">
                {currentLease ? formatLeaseLengthFromDates(new Date(currentLease.leaseStartDate), new Date(currentLease.leaseEndDate)) : '—'}
              </TableCell>
              <TableCell align="right" className="text-sm text-blue-100 font-semibold py-2">
                {formatLeaseLengthFromDates(new Date(draftLease.leaseStartDate), new Date(draftLease.leaseEndDate))}
              </TableCell>
              <TableCell align="right" className="text-sm text-zinc-400 py-2">
                {
                  currentMetrics ? (() => { 
                    const diff = Math.round((draftMetrics.leaseLength - currentMetrics.leaseLength) * 2) / 2;
                    return `${diff > 0 ? '+' : ''}${diff} ${Math.abs(diff) === 1 ? 'month' : 'months'}`;
                  })()
                  : '—'
                }
              </TableCell>
            </TableRow>

            {/* Concessions Row */}
            <TableRow className="border-b border-zinc-700/50">
              <TableCell className="text-xs text-zinc-400 py-2">
                Concessions {showConcessionsAsMultiples ? '(mo)' : '($)'}
              </TableCell>
              <TableCell align="right" className="text-sm text-zinc-300 py-2">
                {currentMetrics
                  ? showConcessionsAsMultiples
                    ? `${concessionsToMonths(currentMetrics.concessions, currentMetrics.monthlyRent)} mo`
                    : `$${getLongNumberStr(Math.round(currentMetrics.concessions))}`
                  : '—'}
              </TableCell>
              <TableCell align="right" className="text-sm text-blue-100 font-semibold py-2">
                {showConcessionsAsMultiples
                  ? `${concessionsToMonths(draftMetrics.concessions, draftMetrics.monthlyRent)} mo`
                  : `$${getLongNumberStr(Math.round(draftMetrics.concessions))}`}
              </TableCell>
              <TableCell align="right" className={`text-sm font-semibold py-2 ${
                currentMetrics
                  ? (draftMetrics.concessions - currentMetrics.concessions) <= 0 ? 'text-green-400' : 'text-red-400'
                  : 'text-zinc-400'
              }`}>
                {currentMetrics
                  ? showConcessionsAsMultiples
                    ? `${(concessionsToMonths(draftMetrics.concessions, draftMetrics.monthlyRent) - concessionsToMonths(currentMetrics.concessions, currentMetrics.monthlyRent)).toFixed(1)} mo`
                    : `$${getLongNumberStr(Math.round(draftMetrics.concessions - currentMetrics.concessions))}`
                  : '—'}
              </TableCell>
            </TableRow>

            {/* Lease End Date Row */}
            <TableRow className="border-b border-zinc-700/50">
              <TableCell className="text-xs text-zinc-400 py-2">Lease End Date</TableCell>
              <TableCell align="right" className="text-sm text-zinc-300 py-2">
                {currentLeaseEndDate ? dayjs(currentLeaseEndDate).format('MM/DD/YYYY') : '—'}
              </TableCell>
              <TableCell align="right" className="text-sm text-blue-100 font-semibold py-2">
                {draftLeaseEndDate ? dayjs(draftLeaseEndDate).format('MM/DD/YYYY') : 'N/A'}
              </TableCell>
              <TableCell align="right" className="text-sm text-zinc-400 py-2">
                {currentLeaseEndDate && draftLeaseEndDate
                  ? `${dayjs(draftLeaseEndDate).diff(dayjs(currentLeaseEndDate), 'day') > 0 ? '+' : ''}${dayjs(draftLeaseEndDate).diff(dayjs(currentLeaseEndDate), 'day')} days`
                  : '—'}
              </TableCell>
            </TableRow>

            {/* Days to Expiry Row */}
            <TableRow>
              <TableCell className="text-xs text-zinc-400 py-2">Days to Expiry</TableCell>
              <TableCell align="right" className="text-sm text-zinc-300 py-2">
                {currentMetrics ? currentDaysToExpiry : '—'}
              </TableCell>
              <TableCell align="right" className="text-sm text-blue-100 font-semibold py-2">
                {draftDaysToExpiry}
              </TableCell>
              <TableCell align="right" className={`text-sm font-semibold py-2 ${
                currentMetrics
                  ? draftDaysToExpiry > currentDaysToExpiry ? 'text-green-400' : 'text-red-400'
                  : 'text-zinc-400'
              }`}>
                {currentMetrics
                  ? `${draftDaysToExpiry > currentDaysToExpiry ? '+' : ''}${draftDaysToExpiry - currentDaysToExpiry}`
                  : '—'}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
